import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { NativeSelect } from '../ui/NativeSelect';
import { universalCompletionService } from '@/services/universalCompletionService';
import type { CompletionStorageStats } from '@/types/lists';
import { Database, Archive, Download, RefreshCw, HardDrive, Calendar, ListChecks } from 'lucide-react';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function CompletionStorageManager() {
  const [stats, setStats] = useState<CompletionStorageStats | null>(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveThreshold, setArchiveThreshold] = useState(30);
  const [archiveResult, setArchiveResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadStats = () => {
    setIsLoading(true);
    try {
      const storageStats = universalCompletionService.getStorageStats();
      setStats(storageStats);
    } catch (error) {
      console.error('Failed to load storage stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadStats();

    // Listen for completion updates
    const handleUpdate = () => loadStats();
    window.addEventListener('universal-completion-updated', handleUpdate);

    return () => {
      window.removeEventListener('universal-completion-updated', handleUpdate);
    };
  }, []);

  const handleArchive = async () => {
    setIsArchiving(true);
    setArchiveResult(null);

    try {
      const result = await universalCompletionService.archiveOldEntries();
      if (result.success) {
        if (result.archivedCount > 0) {
          setArchiveResult({
            success: true,
            message: `Successfully archived ${result.archivedCount} entries. A download should have started automatically.`
          });
        } else {
          setArchiveResult({
            success: true,
            message: 'No entries to archive at this time.'
          });
        }
        loadStats();
      } else {
        setArchiveResult({
          success: false,
          message: `Archive failed: ${result.error || 'Unknown error'}`
        });
      }
    } catch (error) {
      setArchiveResult({
        success: false,
        message: `Archive failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      });
    } finally {
      setIsArchiving(false);
    }
  };

  const handleThresholdChange = (days: number) => {
    setArchiveThreshold(days);
    universalCompletionService.setArchiveThresholdDays(days);
    loadStats();
  };

  const archives = universalCompletionService.getArchiveIndex();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Database className="h-5 w-5 mr-2" />
            Completion Data Storage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-5 w-5 animate-spin mr-2" />
            <span className="text-sm text-muted-foreground">Loading storage statistics...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Database className="h-5 w-5 mr-2" />
          Completion Data Storage
        </CardTitle>
        <CardDescription>
          Manage your task completion history and free up local storage
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Storage Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <ListChecks className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-muted-foreground">Active Entries</span>
            </div>
            <p className="text-lg font-semibold">{stats?.totalEntries || 0}</p>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <HardDrive className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-muted-foreground">Storage Used</span>
            </div>
            <p className="text-lg font-semibold">{formatBytes(stats?.totalBytes || 0)}</p>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-muted-foreground">Date Range</span>
            </div>
            <p className="text-sm font-medium">
              {stats?.oldestEntry && stats?.newestEntry
                ? `${formatDate(stats.oldestEntry)} - ${formatDate(stats.newestEntry)}`
                : 'No data'}
            </p>
          </div>

          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Archive className="h-4 w-4 text-gray-500" />
              <span className="text-xs text-muted-foreground">Archived Files</span>
            </div>
            <p className="text-lg font-semibold">{stats?.archivedFilesCount || 0}</p>
          </div>
        </div>

        {/* Lists with Completion Data */}
        {stats && Object.keys(stats.entriesByList).length > 0 && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-sm font-medium text-blue-900 mb-2">
              Completion data for {Object.keys(stats.entriesByList).length} lists
            </p>
            <p className="text-xs text-blue-700">
              Data is stored per list per day. Recurring lists generate new entries daily.
            </p>
          </div>
        )}

        {/* Archive Settings */}
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Archive Threshold</label>
            <NativeSelect
              value={archiveThreshold}
              onChange={(e) => handleThresholdChange(parseInt(e.target.value, 10))}
            >
              <option value={7}>7 days (keep recent only)</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days (recommended)</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={180}>180 days</option>
              <option value={365}>365 days</option>
            </NativeSelect>
            <p className="text-xs text-muted-foreground mt-1">
              Entries older than this will be archived to reduce localStorage usage.
            </p>
          </div>

          {/* Archivable Count */}
          {stats && stats.archivableCount > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex items-center gap-2">
                <Archive className="h-4 w-4 text-amber-600" />
                <span className="text-sm text-amber-800">
                  <strong>{stats.archivableCount}</strong> entries are older than {archiveThreshold} days and can be archived.
                </span>
              </div>
            </div>
          )}

          {/* Archive Result */}
          {archiveResult && (
            <Alert variant={archiveResult.success ? 'default' : 'destructive'}>
              {archiveResult.message}
            </Alert>
          )}

          {/* Archive Button */}
          <Button
            onClick={handleArchive}
            disabled={isArchiving || (stats?.archivableCount || 0) === 0}
            className="w-full"
          >
            {isArchiving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Archiving...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Archive {stats?.archivableCount || 0} Old Entries
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground">
            Archiving will download a JSON file with your old completion data and remove it from local storage.
            You can re-import archives later if needed.
          </p>
        </div>

        {/* Previous Archives */}
        {archives.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">Previous Archives</h4>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {archives.map((archive, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm"
                >
                  <div>
                    <p className="font-medium text-xs">{archive.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(archive.dateRange.start)} to {formatDate(archive.dateRange.end)} | {archive.entriesCount} entries
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(archive.archivedAt)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Refresh Button */}
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={loadStats}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Stats
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
